import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { baseUrl } from '../../utils/axios'
import { handleErrors, formatParams, getParams } from 'utils/helpers'
import { AxiosResponse } from 'axios'
import { IPaginatedResponse } from 'types/api/general.interface'
import { ITransportationSlice } from 'types/transportation.interface'
import {
  IGetAllTransporationUsersSingleResponse,
  IGetAllTransportationDailyIntentsSingleResponse,
  IPostTransportationUserSettingsRequest,
  ITransferZone,
  ITransportationVehicleRequest,
  ITransportationVehicleSingleResponse,
} from 'types/api/transportationApi.interface'
import { toast } from 'react-toastify'
import { ITransportationVehicleModal } from 'pages/Transportation/TransportationVehicleModal'
import { ITransportationSettingsModalState } from 'pages/Transportation/TransportationSettingsModal'

const initialState: ITransportationSlice = {
  isLoading: false,
  users: {
    data: [],
    totalCount: 0,
    totalPages: 0,
  },
  dailyIntents: {
    data: [],
    totalCount: 0,
    totalPages: 0,
  },
  transferZones: {
    points: [],
    totalCount: 0,
    totalPages: 0,
    noPoints: true,
  },
  vehicles: {
    vehicles: [],
    totalCount: 0,
    totalPages: 0,
    noVehicles: true,
  },
}

export const getTransferZones = createAsyncThunk(
  'transportation/gettransferZones',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<ITransferZone>> = await baseUrl.get(
        'transportation/management/zone',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const addTransportationSettings = createAsyncThunk(
  'transportation/addTransportationSettings',
  async (state: ITransportationSettingsModalState, thunkAPI) => {
    const req: IPostTransportationUserSettingsRequest = {
      direction: state.direction?.value,
      userId: state.user?.value,
      transportationZoneId: state.transportationZone?.value,
    }

    try {
      const resp: AxiosResponse<IPaginatedResponse<ITransferZone>> = await baseUrl.post(
        'transportation/management/user/settings',
        req,
      )
      await thunkAPI.dispatch(getTransportationUsers(getParams({ location })))

      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getTransportationUsers = createAsyncThunk(
  'transportation/getTransportationUsers',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IGetAllTransporationUsersSingleResponse>> =
        await baseUrl.get('transportation/management/users', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getTransportationDailyIntents = createAsyncThunk(
  'transportation/getTransportationDailyIntents',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<
        IPaginatedResponse<IGetAllTransportationDailyIntentsSingleResponse>
      > = await baseUrl.get('transportation/management/daily-intents', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const addTransferZone = createAsyncThunk(
  'transportation/addTransferZone',
  async (state: ITransferZone, thunkAPI) => {
    try {
      const resp: AxiosResponse<ITransferZone> = await baseUrl.post(
        'transportation/management/zone',
        state,
      )
      const params = getParams({
        location,
        limit: 5,
      })
      thunkAPI.dispatch(getTransferZones(params))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const editTransferZone = createAsyncThunk(
  'transportation/editTransferZone',
  async (state: ITransferZone, thunkAPI) => {
    const req = structuredClone(state)
    const { id } = req
    delete req.id
    try {
      const resp: AxiosResponse<ITransferZone> = await baseUrl.patch(
        'transportation/management/zone/' + id,
        req,
      )
      return { ...resp.data, id }
    } catch (error) {
      console.log(error)
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const deleteTransferZone = createAsyncThunk(
  'transportation/deleteTransferZone',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.delete('transportation/management/zone/' + id)
      const params = getParams({
        location,
        limit: 5,
      })
      await thunkAPI.dispatch(getTransferZones(params))
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getTransportationVehicles = createAsyncThunk(
  'transportation/getTransportationVehicles',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<ITransportationVehicleSingleResponse>> =
        await baseUrl.get('transportation/management/vehicle', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const addTransportationVehicle = createAsyncThunk(
  'transportation/addTransportationVehicle',
  async (state: ITransportationVehicleModal, thunkAPI) => {
    try {
      const req: ITransportationVehicleRequest = {
        vehicleType: state.vehicleType.value,
        machineId: state.machine.value,
      }
      const resp: AxiosResponse = await baseUrl.post('transportation/management/vehicle', req)
      const params = getParams({
        location,
        limit: 5,
      })
      thunkAPI.dispatch(getTransportationVehicles(params))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const editTransportationVehicle = createAsyncThunk(
  'transportation/editTransportationVehicle',
  async (state: ITransportationVehicleModal, thunkAPI) => {
    try {
      const req: ITransportationVehicleRequest = {
        vehicleType: state.vehicleType.value,
        machineId: state.machine.value,
      }
      const resp: AxiosResponse<ITransportationVehicleSingleResponse> = await baseUrl.patch(
        'transportation/management/vehicle/' + state.id,
        req,
      )
      return { ...resp.data, id: state.id }
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const deleteTransportationVehicle = createAsyncThunk(
  'transportation/deleteTransportationVehicle',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.delete('transportation/management/vehicle/' + id)
      const params = getParams({
        location,
        limit: 5,
      })
      thunkAPI.dispatch(getTransportationVehicles(params))
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const transportationSlice = createSlice({
  name: 'transportation',
  initialState,
  reducers: {
    clearTransportation: (state) => {
      state.transferZones.points = []
      state.transferZones.totalCount = 0
      state.transferZones.totalPages = 0
      state.transferZones.noPoints = true

      state.vehicles.vehicles = []
      state.vehicles.totalCount = 0
      state.vehicles.totalPages = 0
      state.vehicles.noVehicles = true
    },
    clearTransportationUsers: (state) => {
      state.users.data = []
      state.users.totalCount = 0
      state.users.totalPages = 0
    },

    clearTransportationDailyIntents: (state) => {
      state.dailyIntents.data = []
      state.dailyIntents.totalCount = 0
      state.dailyIntents.totalPages = 0
    },
  },

  extraReducers: {
    [addTransportationSettings.pending.type]: (state) => {
      state.isLoading = true
    },
    [addTransportationSettings.fulfilled.type]: (state: ITransportationSlice) => {
      state.isLoading = false
      toast.success('Transportation settings successfully added')
    },
    [addTransportationSettings.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getTransportationUsers.pending.type]: (state) => {
      state.isLoading = true
    },
    [getTransportationUsers.fulfilled.type]: (
      state: ITransportationSlice,
      { payload }: { payload: IPaginatedResponse<IGetAllTransporationUsersSingleResponse> },
    ) => {
      state.isLoading = false
      state.users = payload
    },
    [getTransportationUsers.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getTransportationDailyIntents.pending.type]: (state) => {
      state.isLoading = true
    },
    [getTransportationDailyIntents.fulfilled.type]: (
      state: ITransportationSlice,
      { payload }: { payload: IPaginatedResponse<IGetAllTransportationDailyIntentsSingleResponse> },
    ) => {
      state.isLoading = false
      state.dailyIntents = payload
    },
    [getTransportationDailyIntents.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getTransferZones.pending.type]: (state) => {
      state.isLoading = true
    },
    [getTransferZones.fulfilled.type]: (
      state: ITransportationSlice,
      { payload }: { payload: IPaginatedResponse<ITransferZone> },
    ) => {
      state.isLoading = false
      state.transferZones.points = payload.data
      state.transferZones.totalCount = payload.totalCount
      state.transferZones.totalPages = payload.totalPages
      state.transferZones.noPoints = state.transferZones.points.length < 1
    },
    [getTransferZones.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [addTransferZone.pending.type]: (state) => {
      state.isLoading = true
    },
    [addTransferZone.fulfilled.type]: (state: ITransportationSlice) => {
      state.isLoading = false
      state.transferZones.noPoints = false
      toast.success('Transfer zone added successfully')
    },
    [addTransferZone.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [editTransferZone.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [editTransferZone.fulfilled.type]: (state: ITransportationSlice) => {
      state.isLoading = false
      toast.success('Transfer zone updated successfully')
    },
    [editTransferZone.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [deleteTransferZone.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [deleteTransferZone.fulfilled.type]: (state: ITransportationSlice) => {
      state.isLoading = false
      toast.success('Transfer zone deleted successfully')
    },
    [deleteTransferZone.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getTransportationVehicles.pending.type]: (state) => {
      state.isLoading = true
    },
    [getTransportationVehicles.fulfilled.type]: (
      state: ITransportationSlice,
      { payload }: { payload: IPaginatedResponse<ITransportationVehicleSingleResponse> },
    ) => {
      state.isLoading = false
      state.vehicles.vehicles = payload.data
      state.vehicles.totalCount = payload.totalCount
      state.vehicles.totalPages = payload.totalPages
      state.vehicles.noVehicles = state.vehicles.vehicles.length < 1
    },
    [getTransportationVehicles.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [addTransportationVehicle.pending.type]: (state) => {
      state.isLoading = true
    },
    [addTransportationVehicle.fulfilled.type]: (state: ITransportationSlice) => {
      state.isLoading = false
      state.vehicles.noVehicles = false
      toast.success('Vehicle added successfully')
    },
    [addTransportationVehicle.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [editTransportationVehicle.pending.type]: (state) => {
      state.isLoading = true
    },
    [editTransportationVehicle.fulfilled.type]: (
      state: ITransportationSlice,
      { payload }: { payload: ITransportationVehicleSingleResponse },
    ) => {
      state.isLoading = false
      state.vehicles.vehicles = state.vehicles.vehicles.map((vehicle) =>
        vehicle.id === payload.id ? payload : vehicle,
      )
      toast.success('Vehicle updated successfully')
    },
    [editTransportationVehicle.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [deleteTransportationVehicle.pending.type]: (state) => {
      state.isLoading = true
    },
    [deleteTransportationVehicle.fulfilled.type]: (state: ITransportationSlice) => {
      state.isLoading = false
      state.vehicles.noVehicles = state.vehicles.vehicles.length < 1
      toast.success('Vehicle deleted successfully')
    },
    [deleteTransportationVehicle.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
  },
})

export const { clearTransportation, clearTransportationUsers, clearTransportationDailyIntents } =
  transportationSlice.actions
export default transportationSlice.reducer
